import React, { useState } from "react";
import { shopsAPI } from "../services/api";
import { useI18n } from "../i18n";
import { useMembership } from "../context/MembershipContext";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

const DEFAULT_DAY = { on: true, open: "09:00", close: "22:00" };

/** The API keeps a list of ranges per day; the form edits one range and a switch. */
const scheduleFromApi = (hours) =>
  Object.fromEntries(
    DAYS.map((day) => {
      const range = hours?.[day]?.[0];
      return [
        day,
        range
          ? { on: true, open: range.open, close: range.close }
          : { ...DEFAULT_DAY, on: !hours },
      ];
    }),
  );

const scheduleToApi = (schedule) =>
  Object.fromEntries(
    DAYS.map((day) => [
      day,
      schedule[day].on
        ? [{ open: schedule[day].open, close: schedule[day].close }]
        : [],
    ]),
  );

const timezones = () => {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    return [];
  }
};

const field =
  "mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500";

/**
 * When the shop is open, and the timezone those times are read in.
 *
 * The menu's open/closed badge is worked out from this in the customer's
 * browser, so it is part of how the shop presents itself — hence Your shop,
 * and the same permission as the rest of it. It saves on its own button, apart
 * from the identity form: a week of times is not something to lose to a typo
 * in the phone number.
 */
const OpeningHours = ({ shop, onSaved }) => {
  const { t } = useI18n();
  const { can } = useMembership();
  const [schedule, setSchedule] = useState(() =>
    scheduleFromApi(shop.opening_hours),
  );
  const [timezone, setTimezone] = useState(shop.timezone || "Africa/Tunis");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const setDay = (day, patch) => {
    setSaved(false);
    setSchedule((current) => ({
      ...current,
      [day]: { ...current[day], ...patch },
    }));
  };

  const save = async () => {
    setBusy(true);
    setError("");
    setSaved(false);

    try {
      const { data } = await shopsAPI.update({
        opening_hours: scheduleToApi(schedule),
        timezone,
      });
      onSaved?.(data.shop);
      setSaved(true);
    } catch (err) {
      setError(
        err.response?.status === 403
          ? t("shop.hours.forbidden")
          : err.response?.data?.message || t("shop.hours.saveFailed"),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
      <fieldset
        disabled={!can("shop.edit")}
        className="min-w-0 border-0 p-0 m-0 space-y-4"
      >
        <div>
          <h2 className="text-lg font-semibold text-gray-800">
            {t("shop.hours.title")}
          </h2>
          <p className="text-sm text-gray-500">{t("shop.hours.hint")}</p>
        </div>

        {error && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">
            {error}
          </p>
        )}
        {saved && (
          <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            {t("common.saved")}
          </p>
        )}

        <ul className="divide-y divide-gray-100">
          {DAYS.map((day) => {
            const row = schedule[day];
            const overnight = row.on && row.close <= row.open;

            return (
              <li
                key={day}
                className="py-3 flex flex-wrap items-center gap-x-4 gap-y-2"
              >
                <label className="flex items-center gap-2 w-40">
                  <input
                    type="checkbox"
                    checked={row.on}
                    onChange={(e) => setDay(day, { on: e.target.checked })}
                    className="h-4 w-4"
                  />
                  <span className="text-sm font-medium text-gray-800">
                    {t(`shop.hours.days.${day}`)}
                  </span>
                </label>

                {row.on ? (
                  <div className="flex flex-wrap items-center gap-2" dir="ltr">
                    <input
                      type="time"
                      required
                      aria-label={t("shop.hours.opensAt")}
                      value={row.open}
                      onChange={(e) => setDay(day, { open: e.target.value })}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg"
                    />
                    <span aria-hidden="true">–</span>
                    <input
                      type="time"
                      required
                      aria-label={t("shop.hours.closesAt")}
                      value={row.close}
                      onChange={(e) => setDay(day, { close: e.target.value })}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg"
                    />
                    {overnight && (
                      <span className="text-xs text-gray-500" dir="auto">
                        {t("shop.hours.overnight")}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-sm text-gray-500">
                    {t("shop.hours.closed")}
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        <button
          type="button"
          onClick={() => {
            setSaved(false);
            setSchedule((current) =>
              Object.fromEntries(DAYS.map((day) => [day, { ...current.mon }])),
            );
          }}
          className="text-sm text-primary-700 hover:underline"
        >
          {t("shop.hours.copyToAll")}
        </button>

        <label className="block md:max-w-sm">
          <span className="text-sm font-medium text-gray-700">
            {t("shop.hours.timezone")}
          </span>
          <select
            value={timezone}
            onChange={(e) => {
              setTimezone(e.target.value);
              setSaved(false);
            }}
            className={field}
            dir="ltr"
          >
            {[...new Set([timezone, ...timezones()])].map((zone) => (
              <option key={zone} value={zone}>
                {zone}
              </option>
            ))}
          </select>
          <span className="mt-1 block text-xs text-gray-500">
            {t("shop.hours.timezoneHint")}
          </span>
        </label>

        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="px-6 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold disabled:opacity-50"
        >
          {busy ? t("common.saving") : t("shop.hours.save")}
        </button>
      </fieldset>
    </div>
  );
};

export default OpeningHours;
